import { useContext, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import { calculateTierProgressionEligibility } from 'loyalty/lib/calculateTierProgressionEligibility'
import {
  generateLoyaltyPointsCalculatorHotelOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import moment from 'moment'
import { getTierRank } from 'loyalty/lib/tierUtils'

interface Params {
  packageViews: Array<App.OfferPackageOptionView>
  offer: App.HotelOffer | App.VillaOffer | App.BundleOffer | undefined
  checkoutDate?: string
  skip?: boolean
}

type PackageViewsTierProgressionResult =
  | {
    showTierProgressionBanner: true
    /** The lowest tier that any package can unlock (e.g., Silver for cheapest package) */
    lowestUnlockableTier: App.LoyaltyTier
    lowestUnlockableTierName: string
    /** The highest tier that any package can reach (e.g., Platinum for most expensive) */
    highestUnlockableTier: App.LoyaltyTier
    highestUnlockableTierName: string
    /** True if lowest !== highest (show "fast-track" messaging) */
    hasMultipleTierJumps: boolean
    isEligible: true
    isLoading: false
    statusReviewDate: string | undefined
    eligiblePackageView: App.OfferPackageOptionView
  }
  | {
    showTierProgressionBanner: false
    lowestUnlockableTier: undefined
    lowestUnlockableTierName: undefined
    highestUnlockableTier: undefined
    highestUnlockableTierName: undefined
    hasMultipleTierJumps: false
    isEligible: boolean
    isLoading: boolean
    statusReviewDate: string | undefined
    eligiblePackageView: undefined
  }

function usePackageViewsTierProgression(params: Params): PackageViewsTierProgressionResult {
  const { packageViews, offer, checkoutDate, skip = false } = params
  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const loyaltyAccountLoading = useAppSelector(
    state => state.loyalty.account.fetching || state.loyalty.account.initial,
  )
  const pendingPoints = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  const availablePackageViews = useMemo(() => {
    return packageViews.filter(view => view.hotelAvailable && view.capacityValid)
  }, [packageViews])

  const calculationRequests = useMemo(() => {
    if (skip || loyaltyAccountLoading || !offer || availablePackageViews.length === 0) {
      return []
    }

    const requests: Array<{ packageIndex: number; type: 'hotel' | 'flight'; request: App.LoyaltyPointsEarnCalculationRequest }> = []
    availablePackageViews.forEach((packageView, packageIndex) => {
      const flightPrice = packageView.flightPrice > 0 ? packageView.flightPrice : undefined

      const hotelRequest = generateLoyaltyPointsCalculatorHotelOptions(offer, {
        packageView,
        currentRegionCode,
        nights: packageView.package.duration,
      })
      if (hotelRequest) {
        requests.push({ packageIndex, type: 'hotel', request: hotelRequest })
      }

      if (flightPrice) {
        const flightRequest = generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
          packageView,
          flightPrice,
        })
        if (flightRequest) {
          requests.push({ packageIndex, type: 'flight', request: flightRequest })
        }
      }
    })
    return requests
  }, [skip, loyaltyAccountLoading, offer, availablePackageViews, currentRegionCode])

  const requestsOnly = useMemo(() => calculationRequests.map(r => r.request), [calculationRequests])

  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(requestsOnly, {
    skipAll: skip || requestsOnly.length === 0,
    useBatch: true,
  })

  const packageCreditsMap = useMemo(() => {
    const map: Record<number, { hotelCredits: number; flightCredits: number }> = {}

    calculationRequests.forEach((requestInfo, index) => {
      const calc = pointsCalculations[index]
      if (!calc?.data) return

      const baseCredits = calc.data.statusCredits ?? 0
      const bonusCredits = calc.data.bonusStatusCredits ?? 0
      const totalCredits = baseCredits + bonusCredits

      if (!map[requestInfo.packageIndex]) {
        map[requestInfo.packageIndex] = { hotelCredits: 0, flightCredits: 0 }
      }

      if (requestInfo.type === 'hotel') {
        map[requestInfo.packageIndex].hotelCredits = totalCredits
      } else {
        map[requestInfo.packageIndex].flightCredits = totalCredits
      }
    })

    return map
  }, [calculationRequests, pointsCalculations])

  const result = useMemo((): PackageViewsTierProgressionResult => {
    const anyLoading = pointsCalculations.some(calc => calc?.initial || calc?.fetching)
    // Also check if we have ANY data - if no calculations have data yet, we're still loading
    const hasAnyData = pointsCalculations.some(calc => calc?.data)
    const isLoading = loyaltyAccountLoading || anyLoading || (pointsCalculations.length > 0 && !hasAnyData) || !tierThresholds

    if (skip || isLoading || !loyaltyAccount || availablePackageViews.length === 0 || !tierThresholds) {
      return {
        showTierProgressionBanner: false,
        lowestUnlockableTier: undefined,
        lowestUnlockableTierName: undefined,
        highestUnlockableTier: undefined,
        highestUnlockableTierName: undefined,
        hasMultipleTierJumps: false,
        isEligible: false,
        isLoading: !skip && isLoading,
        statusReviewDate: loyaltyAccount?.statusReviewDate,
        eligiblePackageView: undefined,
      }
    }

    const pointsEarnDate = checkoutDate
      ? moment(checkoutDate).add(1, 'day').format('YYYY-MM-DD')
      : undefined

    // Track across ALL packages:
    // - globalLowestTier: the lowest tier any package can unlock
    // - globalHighestTier: the highest tier any package can reach
    let globalLowestTier: App.LoyaltyTier | undefined
    let globalHighestTier: App.LoyaltyTier | undefined
    let bestPackageView: App.OfferPackageOptionView | undefined

    for (let i = 0; i < availablePackageViews.length; i++) {
      const packageView = availablePackageViews[i]
      const packageCredits = packageCreditsMap[i]

      if (!packageCredits) continue

      const hotelStatusCredits = packageCredits.hotelCredits
      const flightStatusCredits = packageCredits.flightCredits
      const totalStatusCredits = hotelStatusCredits + flightStatusCredits
      const packageNights = packageView.package.duration

      const eligibility = calculateTierProgressionEligibility({
        currentTier: loyaltyAccount.tier,
        currentStatusCredits: loyaltyAccount.statusCredits ?? 0,
        currentNights: loyaltyAccount.numberOfNights ?? 0,
        statusCredits: totalStatusCredits,
        nights: packageNights,
        pointsEarnDate,
        statusReviewDate: loyaltyAccount.statusReviewDate,
        pendingPoints: pendingPoints ?? [],
        tierThresholds,
      })

      if (eligibility.isEligible) {
        // Track the LOWEST unlockable tier across all packages
        const currentLowestRank = getTierRank(globalLowestTier)
        const newLowestRank = getTierRank(eligibility.lowestUnlockableTier)
        if (currentLowestRank === -1 || newLowestRank < currentLowestRank) {
          globalLowestTier = eligibility.lowestUnlockableTier
        }

        // Track the HIGHEST unlockable tier across all packages
        const currentHighestRank = getTierRank(globalHighestTier)
        const newHighestRank = getTierRank(eligibility.highestUnlockableTier)
        if (newHighestRank > currentHighestRank) {
          globalHighestTier = eligibility.highestUnlockableTier
          bestPackageView = packageView
        }
      }
    }

    // Return combined result if we found any eligible packages
    if (globalLowestTier && globalHighestTier && bestPackageView) {
      const hasMultipleTierJumps = globalLowestTier !== globalHighestTier

      return {
        showTierProgressionBanner: true,
        lowestUnlockableTier: globalLowestTier,
        lowestUnlockableTierName: getLoyaltyTierDisplayName(globalLowestTier),
        highestUnlockableTier: globalHighestTier,
        highestUnlockableTierName: getLoyaltyTierDisplayName(globalHighestTier),
        hasMultipleTierJumps,
        isEligible: true,
        isLoading: false,
        statusReviewDate: loyaltyAccount.statusReviewDate,
        eligiblePackageView: bestPackageView,
      }
    }

    return {
      showTierProgressionBanner: false,
      lowestUnlockableTier: undefined,
      lowestUnlockableTierName: undefined,
      highestUnlockableTier: undefined,
      highestUnlockableTierName: undefined,
      hasMultipleTierJumps: false,
      isEligible: false,
      isLoading: false,
      statusReviewDate: loyaltyAccount.statusReviewDate,
      eligiblePackageView: undefined,
    }
  }, [skip, loyaltyAccountLoading, loyaltyAccount, availablePackageViews, packageCreditsMap, pointsCalculations, checkoutDate, pendingPoints, tierThresholds])

  return result
}

export default usePackageViewsTierProgression
